<template>
    <div class="mt-3">
        <div class="is-medium mb-2 has-text-weight-bold">Stock Item {{ product_id }} History</div>
        <div class="columns">
            <div class="column is-6">
                <div class="is-flex is-justify-content">
                    <div class="mr-1">
                        <h6 class="subtitle is-6" style="margin-bottom: 15px">
                            {{ __('strings.product.stock_history.year') }}</h6>
                        <b-select name="year" v-model="params.year" @input="refreshChartData()">
                            <option v-for="(year, index) in years"
                                    :key="index" v-if="!isUndefined(year)" :value="year">{{ year }}
                            </option>
                        </b-select>
                    </div>
                    <div class="mr-1">
                        <h6 class="subtitle is-6" style="margin-bottom: 15px">
                            {{ __('strings.product.stock_history.month') }}</h6>
                        <b-select name="month" v-model="params.month" @input="refreshChartData()"
                                  v-bind:disabled="params.yearlyAverage">
                            <option value="null">All months</option>
                            <option v-for="(month, index) in allMonths"
                                    :key="index" :value="index+1">{{ month }}
                            </option>
                        </b-select>
                    </div>
                    <div class="">
                        <h6 class="subtitle is-6" style="margin-bottom: 15px">
                            {{ __('strings.product.stock_history.day') }}</h6>
                        <b-select name="day" v-model="params.day" @input="refreshChartData()"
                                  v-bind:disabled="params.yearlyAverage">
                            <option value="null">All days</option>
                            <option v-for="(day, index) in allDays"
                                    :key="index" :value="day">{{ day }}
                            </option>
                        </b-select>
                    </div>
                </div>
            </div>
            <div class="column is-2"></div>
            <div class="column is-4">
                <div class="is-clearfix">
                    <div class="is-pulled-left">
                    </div>
                    <div class="is-pulled-right">
                        <h6 class="subtitle is-6" style="margin-bottom: 15px">
                            {{ __('strings.product.stock_history.yearly_average') }}</h6>
                        <b-switch v-model="params.yearlyAverage" @input="refreshChartData"></b-switch>
                    </div>
                </div>
            </div>
        </div>
        <p class="content p-1 is-small has-background-link-light has-text-black" style="display: inline-block"
           v-if="chartLegend.length > 0" v-html="chartLegend"></p>
        <line-chart v-if="loaded" :chart-data="chartData" :options="options"/>
        <div class="chart-loader" v-if="loading || !loaded">
            <b-loading :is-full-page="false" v-model="loading"/>
        </div>
    </div>
</template>
<script>
import LineChart from '../chart/Chart';
import moment from 'moment';

export default {
    name: 'LineChartContainer',
    components: {LineChart},
    props: {
        accordionState: false,
        product_id: {
            required: true
        },
        retailer_id: {
            required: true
        }
    },
    data() {
        return {
            loaded: false,
            loading: false,
            filterByDateRange: false,
            chartLegend: "",
            chartData: {
                labels: [],
                datasets: []
            },
            options: {
                scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true
                        },
                        gridLines: {
                            display: true
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Qty'
                        }
                    }],
                    xAxes: [{
                        gridLines: {
                            display: false
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Days'
                        }
                    }]
                },
                legend: {
                    display: true
                },
                lineTension: 0,
                responsive: true,
                maintainAspectRatio: false
            },
            params: {
                product_id: this.product_id,
                year: new Date().getFullYear(),
                month: new Date().getMonth() + 1,
                day: new Date().getDate(),
                yearlyAverage: false,
            },
            years: [],
            allMonths: moment.months(),
            allDays: []
        }
    },
    async mounted() {
        this.loaded = false;
        this.loading = !this.loaded;
        this.params.product_id = this.product_id;
        this.params.retailer_id = this.retailer_id;
        await axios.get('/stock-history/product/', {params: this.params})
            .then(response => {
                var responseData = response.data;
                this.updateGeneralChartOptions(responseData);
                if (responseData.hasOwnProperty('multiple_months') && responseData.multiple_months === true) {
                    this.resetDataSets();
                    this.processMultipleDataSets(responseData);
                } else {
                    this.resetDataSets();
                    this.processSingleDataSets(responseData);
                }
                this.loaded = true;
                this.loading = !this.loaded;
            });
    },
    beforeMount() {
        for (let i = 2018; i <= new Date().getFullYear(); i++) {
            this.years.push(i);
        }
        this.allDays = this.getDaysInSelectedMonth();
    },
    methods: {
        refreshChartData() {
            this.allDays = this.getDaysInSelectedMonth();
            this.loaded = false;
            this.loading = !this.loaded;
            if (this.params.month === "null") {
                this.params.day = "null";
                this.allDays = [];
            }
            this.params.product_id = this.product_id;
            this.params.retailer_id = this.retailer_id;
            axios.get('/stock-history/product/', {params: this.params})
                .then(response => {
                    var responseData = response.data;
                    this.updateGeneralChartOptions(responseData);
                    if (responseData.hasOwnProperty('multiple_months') && responseData.multiple_months === true) {
                        this.resetDataSets();
                        this.processMultipleDataSets(responseData);
                    } else {
                        this.resetDataSets();
                        this.processSingleDataSets(responseData);
                    }
                    this.loaded = true;
                    this.loading = !this.loaded;
                });
        },
        processSingleDataSets(responseData) {
            var datasets = responseData;
            if (responseData.hasOwnProperty('datasets')) {
                datasets = responseData.datasets;
            }
            var chartLabels = [];
            var _data = {
                data: []
            };
            if (datasets.hasOwnProperty('data')) {
                for (const [day, qty] of Object.entries(datasets['data'])) {
                    chartLabels.push(day);
                    _data.data.push(qty);
                }
            }
            for (const [key, value] of Object.entries(datasets)) {
                if (key !== "data" && key !== "multiple_months") {
                    _data[key] = value;
                }
            }
            this.$set(this.chartData.datasets, 0, _data);
            this.$set(this.chartData, 'labels', chartLabels);
        },
        processMultipleDataSets(responseData) {
            var datasets = responseData.datasets;
            var chartLabels = [];
            for (const [month, dataset] of Object.entries(datasets)) {
                if (dataset.hasOwnProperty('data')) {
                    for (const [day, qty] of Object.entries(dataset['data'])) {
                        if (day > 0) {
                            chartLabels[day-1] = day;
                        }
                    }
                    dataset['data'] = Object.values(dataset['data']);
                }
                this.$set(this.chartData.datasets, month, dataset);
            }

            this.$set(this.chartData, 'labels', chartLabels);
        },
        resetDataSets() {
            this.chartData.datasets = [];
        },
        getRealDate(date) {
            return new Date(date).toLocaleDateString().slice(0, 10);
        },
        updateGeneralChartOptions(responseData) {
            this.chartLegend = this.getPropertyValue(responseData, 'chartLegend');
            this.options.scales.xAxes[0].scaleLabel.labelString = this.getPropertyValue(responseData, 'xAxisLabel');
            this.chartData.labels = [];
        },
        getDaysInSelectedMonth() {
            return Array.from({length: moment(this.params.year.toString() + this.params.month.toString(), "YYYYM").daysInMonth()}, (_, i) => i + 1)
        },
    },
    watch: {
        accordionState() {
            this.refreshChartData();
        },
        product_id() {
            this.refreshChartData();
        },
        retailer_id() {
            this.refreshChartData();
        }
    }
}
</script>
